import { LocalAudioStats } from './reportMediaError';

/** How long after `SessionState.Established` the first verdict is taken. */
export const MIC_CHECK_INITIAL_DELAY_MS = 10000;

/** How often the microphone is re-checked for the rest of the call. */
export const MIC_CHECK_INTERVAL_MS = 5000;

/**
 * How many consecutive dead 5s windows are needed to raise the warning AFTER the first check.
 * The first check already covers the 10s since establish, so it stands on its own; a later
 * verdict is built out of two 5s windows to carry the same weight of evidence.
 */
export const MIC_DEAD_CONSECUTIVE_SAMPLES = 2;

/**
 * How many consecutive silent windows are needed once this microphone has already been heard on
 * this call, and the only complaint is silence rather than a fault on the track itself.
 *
 * A device that has demonstrably worked deserves more benefit of the doubt - an aggressive
 * hardware noise gate on a quiet line is the plausible innocent explanation. It is not a licence
 * to ignore the device forever though: a driver that starts handing over zeroed buffers mid-call
 * is a real failure, and 20 seconds of unbroken digital silence is not something a noise gate
 * produces. Track-level faults are exempt and use the shorter threshold above.
 */
export const MIC_DEAD_CONSECUTIVE_SAMPLES_AFTER_AUDIO = 4;

export type MicSignalReason =
  | 'track-ended'
  | 'track-muted'
  | 'no-samples'
  | 'zero-energy';

export interface MicSignalVerdict {
  /** The microphone is producing no audio, for one of the hard reasons below. */
  dead: boolean;
  /**
   * Whether a verdict could be reached at all. `false` means the counters were missing, which
   * happens wherever there is no `media-source` report (non-Chromium). Not judgable is NOT dead:
   * the caller must leave the user alone.
   */
  usable: boolean;
  /** Non-empty when `dead`, using the same vocabulary as the Sentry `deadReason` tag. */
  reasons: MicSignalReason[];
  /** The window produced audio. Latches `everHadAudio` in the caller. */
  hadAudio: boolean;
  /**
   * At least one reason is a fault read straight off the track or the sample clock, rather than
   * merely a silent window. Unambiguous, so the caller needs less of it before speaking up.
   */
  deviceFault: boolean;
}

const delta = (
  prev: number | null | undefined,
  next: number | null | undefined
): number | null => {
  if (!Number.isFinite(prev as number) || !Number.isFinite(next as number)) {
    return null;
  }

  // Cumulative counters never decrease, but a `replaceTrack` restarts them from zero. Treating
  // that as "no growth" would be a false positive, so a negative delta is reported as unknown
  // and the caller re-seeds its baseline.
  const diff = (next as number) - (prev as number);

  return diff < 0 ? null : diff;
};

/**
 * @name evaluateMicSignal
 * @description Decide whether the local microphone produced audio between two snapshots of
 * `getLocalAudioStats`. Pure, so the whole false-positive contract is unit-testable.
 *
 * `totalAudioEnergy` and `totalSamplesDuration` are cumulative, so only the growth between the
 * two samples says anything about now. At the first check the caller passes a zeroed `prev`,
 * which makes the window the whole 10s since the call was established.
 *
 * Only hard faults count. A live capture device always accrues a room-noise floor, so energy
 * keeps climbing even in a silent room - low-but-nonzero is a quiet user, not a broken
 * microphone, and never reported. Exactly zero growth WHILE samples flow is different: the
 * source is handing over zeroed buffers.
 *
 * This reports what one window shows and nothing more. How much of it to believe before telling
 * the user is the caller's policy, driven by `deviceFault` and the two thresholds above.
 */
export function evaluateMicSignal(
  prev: LocalAudioStats,
  next: LocalAudioStats
): MicSignalVerdict {
  const reasons: MicSignalReason[] = [];

  // Per spec a track's readyState is only ever 'live' or 'ended'.
  if (next.trackReadyState === 'ended') reasons.push('track-ended');
  if (next.trackMuted === true) reasons.push('track-muted');

  const samples = delta(prev.totalSamplesDuration, next.totalSamplesDuration);
  const energy = delta(prev.totalAudioEnergy, next.totalAudioEnergy);
  const judgable = samples !== null && energy !== null;

  if (judgable) {
    if (samples === 0) {
      reasons.push('no-samples');
    } else if (energy === 0) {
      reasons.push('zero-energy');
    }
  }

  // The device-level faults stand on their own: they are read straight off the track and need
  // no counters, so a snapshot with no `media-source` report can still be judged dead by them.
  const usable = judgable || reasons.length > 0;

  return {
    dead: reasons.length > 0,
    usable,
    reasons,
    hadAudio: judgable && (energy as number) > 0,
    deviceFault: reasons.some(reason => reason !== 'zero-energy'),
  };
}

export default evaluateMicSignal;
